import { Node } from '@xyflow/react';

import { ActivityMetadataModel, ParameterType } from '../../../api/api';
import { uuid } from '../../../common/uuid';
import { ActivityTypes } from '../Components/ActivityIcon';
import { nodeFromActivity } from '../Components/Nodes/ActivityNode';
import { nodeFromBlock } from '../Components/Nodes/BlockNode';
import { nodeFromGroup } from '../Components/Nodes/GroupNode';
import { nodeFromNote } from '../Components/Nodes/NoteNode';
import { nodeFromOnError } from '../Components/Nodes/OnErrorNode';
import { nodeFromSection } from '../Components/Nodes/SectionNode';
import { edgeFromTransition } from '../Components/Nodes/TransitionEdge';
import { ActivityModel, FlowDefinitionModel, TransitionModel } from '../Models/FlowModel';
import { FlowEdge, FlowNode } from '../Models/GraphModel';
import { SchemaValueModel } from '../Models/SchemaValueModel';
import { isBlockToolContentHidden } from './AgentServiceCompact';

export default class FlowClipboardService {
	private activityByName: Map<string, ActivityMetadataModel>;

	constructor(activityByName: Map<string, ActivityMetadataModel> = new Map()) {
		this.activityByName = activityByName;
	}

	private isBlock(type: string): boolean {
		return this.activityByName.get(type)?.isBlock === true;
	}

	private isNote(type: string): boolean {
		return type === ActivityTypes.NOTE;
	}

	private isSection(type: string): boolean {
		return type === ActivityTypes.SECTION;
	}

	private isOnError(type: string): boolean {
		return type === ActivityTypes.ON_ERROR;
	}

	private isGroupTool(type: string): boolean {
		return type === ActivityTypes.GROUP_TOOL;
	}

	private isCollapsedBlockTool(node: FlowNode): boolean {
		return isBlockToolContentHidden(node.data);
	}

	/**
	 * Gets the names of parameters that are of type SchemaValue for a given activity type
	 */
	private getSchemaValueParameterNames(activityType: string): Set<string> {
		const metadata = this.activityByName.get(activityType);
		if (!metadata) return new Set();

		return new Set(metadata.parameters.filter((param) => param.type === ParameterType.SchemaValue).map((param) => param.name));
	}

	/**
	 * Checks if a value is a SchemaValueModel (has Id, Name, and Value properties)
	 */
	private isSchemaValue(value: unknown): value is SchemaValueModel {
		return (
			typeof value === 'object' &&
			value !== null &&
			'Id' in value &&
			'Value' in value &&
			!('Type' in value) &&
			typeof (value as { Id: unknown }).Id === 'string' &&
			typeof (value as { Value: unknown }).Value === 'object'
		);
	}

	private remapSchemaValueIds(value: unknown, sharedIdMap = new Map<string, string>()): unknown {
		if (this.isSchemaValue(value)) {
			return {
				...value,
				Id: sharedIdMap.get(value.Id) ?? uuid(),
				Value: this.remapSchemaValueIds(value.Value, sharedIdMap),
			};
		}

		if (Array.isArray(value)) {
			return value.map((item) => this.remapSchemaValueIds(item, sharedIdMap));
		}

		if (typeof value === 'object' && value !== null) {
			if ('SchemaValueId' in value && typeof value.SchemaValueId === 'string') {
				return { ...value, SchemaValueId: sharedIdMap.get(value.SchemaValueId) ?? value.SchemaValueId };
			}
			return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, this.remapSchemaValueIds(item, sharedIdMap)]));
		}

		return value;
	}

	/**
	 * Recursively processes activity properties and regenerates IDs for any SchemaValue properties
	 * (inline schema definitions) based on the activity's parameter metadata.
	 * Note: SchemaRef properties are NOT regenerated as they are references to existing schemas.
	 */
	private regenerateSchemaValueIds(obj: Record<string, unknown>, activityType: string): Record<string, unknown> {
		const schemaValueParams = this.getSchemaValueParameterNames(activityType);
		const result: Record<string, unknown> = { ...obj };

		for (const key in result) {
			// Skip system properties
			if (key === 'Id' || key === 'Type' || key === 'Metadata' || key === 'Transitions' || key === 'Content') {
				continue;
			}

			const value = result[key];

			// Regenerate ID for SchemaValue parameters (inline schemas)
			if (schemaValueParams.has(key) && this.isSchemaValue(value)) {
				result[key] = { ...value, Id: uuid() };
			}
			// Handle array properties (for isCollection parameters)
			else if (Array.isArray(value) && schemaValueParams.has(key)) {
				result[key] = value.map((item) => {
					if (this.isSchemaValue(item)) {
						return { ...item, Id: uuid() };
					}
					return item;
				});
			}
		}
		return result;
	}

	// ─── Copy ────────────────────────────────────────────────────────────────────

	/**
	 * Builds a serialisable clipboard model from a root React Flow node.
	 * Transitions are read from the live edge list; each node's current position is
	 * written into Metadata.x/y so paste can restore it exactly.
	 */
	buildClipboardModel(rootId: string, allNodes: Node<ActivityModel>[], allEdges: FlowEdge[]): ActivityModel | undefined {
		const rootNode = allNodes.find((n) => n.id === rootId);
		if (!rootNode) return undefined;
		return this._buildModelTree(rootNode, allNodes, allEdges);
	}

	private _buildModelTree(rootNode: Node<ActivityModel>, allNodes: Node<ActivityModel>[], allEdges: FlowEdge[]): ActivityModel {
		const children = allNodes.filter((n) => n.parentId === rootNode.id);
		const transitions = allEdges.filter((e) => e.source === rootNode.id && e.data).map((e) => e.data as TransitionModel);
		const model: ActivityModel = {
			...rootNode.data,
			// Always write the live position into Metadata so paste/save can restore it
			Metadata: { ...rootNode.data.Metadata, x: rootNode.position.x, y: rootNode.position.y },
			Transitions: transitions,
		};
		if (children.length > 0) {
			model.Content = children.map((child) => this._buildModelTree(child, allNodes, allEdges));
		}
		return model;
	}

	// ─── Paste ───────────────────────────────────────────────────────────────────

	/**
	 * Parses clipboard text and, if valid, resolves the paste position and builds
	 * the React Flow nodes + edges ready to be spliced into the canvas.
	 *
	 * Position priority:
	 *   1. A selected block node that is not the same type as the pasted model
	 *      → paste inside that block at its centre.
	 *   2. Metadata.x/y stored during copy → restore with a +20 px offset.
	 *   3. `fallbackPosition` (viewport centre supplied by the caller).
	 *
	 * Returns `null` when the text is not a valid, known activity model.
	 */
	parseAndBuildPasteResult(
		text: string,
		selectedNodes: FlowNode[],
		fallbackPosition: { x: number; y: number },
	): { nodes: FlowNode[]; edges: FlowEdge[] } | null {
		let parsed: unknown;
		try {
			parsed = JSON.parse(text);
		} catch {
			return null;
		}

		if (!parsed || typeof parsed !== 'object' || !('Type' in parsed) || typeof (parsed as ActivityModel).Type !== 'string') {
			return null;
		}

		const model = parsed as ActivityModel;
		if (!this.activityByName.has(model.Type)) return null;

		const selectedBlockNode = selectedNodes.find((n) => this.isBlock((n.data as ActivityModel).Type));

		let pasteX: number;
		let pasteY: number;
		let pasteParentId: string | undefined;
		let pasteParentHidesChildren = false;

		if (selectedBlockNode) {
			pasteX = (selectedBlockNode.measured?.width ?? 200) / 2;
			pasteY = (selectedBlockNode.measured?.height ?? 200) / 2;
			pasteParentId = selectedBlockNode.id;
			pasteParentHidesChildren = this.isGroupTool((selectedBlockNode.data as ActivityModel).Type) || this.isCollapsedBlockTool(selectedBlockNode);
		} else {
			const storedX = model.Metadata?.x as number | undefined;
			const storedY = model.Metadata?.y as number | undefined;
			if (storedX !== undefined && storedY !== undefined) {
				pasteX = storedX + 20;
				pasteY = storedY + 20;
			} else {
				pasteX = fallbackPosition.x;
				pasteY = fallbackPosition.y;
			}
			pasteParentId = undefined;
		}

		return this.buildPasteResult(model, pasteX, pasteY, pasteParentId, pasteParentHidesChildren);
	}

	/**
	 * Turns a clipboard model back into React Flow nodes + edges.
	 * Every activity receives a fresh ID; edges within the subtree are remapped accordingly.
	 */
	buildPasteResult(
		model: ActivityModel,
		x: number,
		y: number,
		parentId?: string,
		parentHidesChildren: boolean = false,
	): { nodes: FlowNode[]; edges: FlowEdge[] } {
		const idMap = new Map<string, string>();
		this._buildIdMap(model, idMap);
		const nodes = this._createNodes(model, x, y, idMap, parentId, parentHidesChildren);
		const edges = this._createEdges(model, idMap);
		return { nodes, edges };
	}

	private _buildIdMap(model: ActivityModel, idMap: Map<string, string>): void {
		idMap.set(model.Id, uuid());
		if (Array.isArray(model.Content)) {
			model.Content.forEach((child: ActivityModel) => this._buildIdMap(child, idMap));
		}
	}

	private _createNodes(
		model: ActivityModel,
		x: number,
		y: number,
		idMap: Map<string, string>,
		parentId?: string,
		parentHidesChildren: boolean = false,
	): FlowNode[] {
		// Regenerate SchemaValue IDs in the model properties based on activity metadata
		const modelWithRegeneratedRefs = this.regenerateSchemaValueIds(model, model.Type) as ActivityModel;

		const freshModel: ActivityModel = {
			...modelWithRegeneratedRefs,
			Id: idMap.get(model.Id) ?? uuid(),
			Transitions: [],
		};

		const isBlock = this.isBlock(freshModel.Type);
		const isNote = this.isNote(freshModel.Type);
		const isSection = this.isSection(freshModel.Type);
		const isOnError = this.isOnError(freshModel.Type);
		const isGroupTool = this.isGroupTool(freshModel.Type);
		const node = isNote
			? (nodeFromNote(freshModel, x, y) as FlowNode)
			: isSection
				? (nodeFromSection(freshModel, x, y) as FlowNode)
				: isOnError
					? (nodeFromOnError(freshModel, x, y) as FlowNode)
					: isGroupTool
						? (nodeFromGroup(freshModel, x, y) as FlowNode)
						: isBlock
							? (nodeFromBlock(freshModel, x, y) as FlowNode)
							: (nodeFromActivity(freshModel, x, y) as FlowNode);

		if (parentId) {
			(node as FlowNode & { parentId: string; extent: string }).parentId = parentId;
			(node as FlowNode & { extent: string }).extent = 'parent';
			(node as FlowNode).hidden = parentHidesChildren;
		}

		const children = model.Content as ActivityModel[] | undefined;
		if (!Array.isArray(children) || children.length === 0) return [node];

		let fallbackOffsetY = 60;
		const fallbackOffsetX = 20;
		const fallbackSpacing = 80;
		const childNodes: FlowNode[] = [];

		for (const child of children) {
			// Metadata.x/y holds the parent-relative position written during copy
			const childX = (child.Metadata?.x as number | undefined) ?? fallbackOffsetX;
			const childY = (child.Metadata?.y as number | undefined) ?? fallbackOffsetY;
			childNodes.push(
				...this._createNodes(child, childX, childY, idMap, node.id, parentHidesChildren || isGroupTool || isBlockToolContentHidden(freshModel)),
			);
			fallbackOffsetY += fallbackSpacing;
		}

		return [node, ...childNodes];
	}

	private _createEdges(model: ActivityModel, idMap: Map<string, string>): FlowEdge[] {
		const newSourceId = idMap.get(model.Id) ?? model.Id;
		const transitions = (model.Transitions ?? []) as TransitionModel[];

		const edges: FlowEdge[] = transitions
			.filter((t) => idMap.has(t.To))
			.map((t) => edgeFromTransition({ ...t, Id: uuid(), To: idMap.get(t.To)! }, { ...model, Id: newSourceId }));

		const children = Array.isArray(model.Content) ? model.Content as ActivityModel[] : [];
		children.forEach((child) => edges.push(...this._createEdges(child, idMap)));
		return edges;
	}

	/**
	 * Creates a copy of a full flow definition with fresh IDs for the definition,
	 * activities, transitions, transition targets, and inline SchemaValue models.
	 */
	remapFlowDefinitionIds(flowDefinition: FlowDefinitionModel): FlowDefinitionModel {
		const idMap = new Map<string, string>();
		const sharedIdMap = new Map((flowDefinition.SchemaValues ?? []).map((schema) => [schema.Id, uuid()]));
		this.collectActivityIdMap(flowDefinition.Content ?? [], idMap);

		return {
			...(this.remapSchemaValueIds(flowDefinition, sharedIdMap) as FlowDefinitionModel),
			Id: uuid(),
			Content: flowDefinition.Content?.map((activity) => this.remapActivityIds(activity, idMap, sharedIdMap)) ?? [],
		};
	}

	private collectActivityIdMap(activities: ActivityModel[], idMap: Map<string, string>) {
		for (const activity of activities) {
			idMap.set(activity.Id, uuid());
			const content = activity.Content as ActivityModel[] | undefined;
			if (Array.isArray(content)) {
				this.collectActivityIdMap(content, idMap);
			}
		}
	}

	private remapActivityIds(activity: ActivityModel, idMap: Map<string, string>, sharedIdMap: Map<string, string>): ActivityModel {
		const content = activity.Content as ActivityModel[] | undefined;
		const transitions = activity.Transitions as TransitionModel[] | undefined;
		const remappedActivity = this.remapSchemaValueIds(activity, sharedIdMap) as ActivityModel;

		return {
			...remappedActivity,
			Id: idMap.get(activity.Id) ?? uuid(),
			Transitions: transitions?.map((transition) => ({
				...transition,
				Id: uuid(),
				To: idMap.get(transition.To) ?? transition.To,
				InputOverrides: transition.InputOverrides?.map((inputOverride) => ({
					...inputOverride,
					ActivityId: idMap.get(inputOverride.ActivityId) ?? inputOverride.ActivityId,
				})),
			})),
			// Content also holds message expressions on non-container activities.
			...(Array.isArray(content) ? { Content: content.map((child) => this.remapActivityIds(child, idMap, sharedIdMap)) } : {}),
		};
	}

	// ─── Clone ───────────────────────────────────────────────────────────────────

	/**
	 * Duplicates a node subtree (root + all descendants) with fresh IDs.
	 * The root is offset by `offsetX`/`offsetY`; children keep their relative positions.
	 * Only intra-subtree edges are cloned.
	 */
	buildCloneResult(
		rootId: string,
		allNodes: Node<ActivityModel>[],
		allEdges: FlowEdge[],
		offsetX = 50,
		offsetY = 50,
	): { nodes: FlowNode[]; edges: FlowEdge[] } {
		const rootNode = allNodes.find((n) => n.id === rootId);
		if (!rootNode) return { nodes: [], edges: [] };

		const descendants = this._collectDescendants(rootId, allNodes);

		const idMap = new Map<string, string>([[rootId, uuid()]]);
		descendants.forEach((n) => idMap.set(n.id, uuid()));

		const newRootId = idMap.get(rootId)!;
		const clonedRoot: Node<ActivityModel> = {
			...rootNode,
			id: newRootId,
			data: { ...rootNode.data, Id: newRootId, Transitions: [] },
			position: { x: rootNode.position.x + offsetX, y: rootNode.position.y + offsetY },
			selected: false,
		};

		const clonedDescendants = descendants.map((n) => {
			const newId = idMap.get(n.id)!;
			return {
				...n,
				id: newId,
				parentId: n.parentId ? (idMap.get(n.parentId) ?? n.parentId) : undefined,
				data: { ...n.data, Id: newId, Transitions: [] },
				selected: false,
			};
		});

		const clonedEdges = allEdges
			.filter((e) => idMap.has(e.source) && idMap.has(e.target))
			.map((e) => ({
				...e,
				id: uuid(),
				source: idMap.get(e.source)!,
				target: idMap.get(e.target)!,
				data: e.data
					? {
						...(e.data as TransitionModel),
						Id: uuid(),
						InputOverrides: (e.data as TransitionModel).InputOverrides?.map((inputOverride) => ({
							...inputOverride,
							ActivityId: idMap.get(inputOverride.ActivityId) ?? inputOverride.ActivityId,
						})),
					}
					: e.data,
			}));

		return {
			nodes: [clonedRoot as FlowNode, ...(clonedDescendants as FlowNode[])],
			edges: clonedEdges,
		};
	}

	// ─── Delete ──────────────────────────────────────────────────────────────────

	/**
	 * Returns the set of node IDs to remove when deleting a node and all its descendants.
	 */
	collectSubtreeIds(rootId: string, allNodes: Node<ActivityModel>[]): Set<string> {
		return new Set([rootId, ...this._collectDescendants(rootId, allNodes).map((n) => n.id)]);
	}

	// ─── Helpers ─────────────────────────────────────────────────────────────────

	private _collectDescendants(nodeId: string, allNodes: Node<ActivityModel>[]): Node<ActivityModel>[] {
		const children = allNodes.filter((n) => n.parentId === nodeId);
		return children.flatMap((child) => [child, ...this._collectDescendants(child.id, allNodes)]);
	}
}
